<script lang="ts">
	import { submitEnrollment } from '$lib/api/formSubmissions';
	import Turnstile from '$lib/components/Turnstile.svelte';
	import LocalSessionTime from '$lib/components/LocalSessionTime.svelte';
	import { PUBLIC_TURNSTILE_ENABLED } from '$env/static/public';
	import type { CohortCourse } from '$lib/types/cohort-course';

	interface Props {
		course: CohortCourse;
		icsHref: string;
		onClose: () => void;
	}

	let { course, icsHref, onClose }: Props = $props();

	const turnstileEnabled = PUBLIC_TURNSTILE_ENABLED === 'true';

	let name = $state('');
	let email = $state('');
	let phone = $state('');
	let honeypot = $state('');
	let errors = $state<Record<string, string>>({});
	let formState = $state<'idle' | 'submitting' | 'success' | 'error'>('idle');
	let errorMessage = $state('');
	let turnstileToken = $state('');
	let turnstileRef = $state<Turnstile | undefined>(undefined);
	let nameInput = $state<HTMLInputElement | undefined>(undefined);

	const inputClass = (hasError: boolean) =>
		`w-full min-h-12 touch-manipulation px-4 py-3 bg-ak-surface-overlay border ${hasError ? 'border-ak-error' : 'border-ak-border-subtle'} rounded-md text-ak-text-primary placeholder-ak-text-muted focus:outline-none focus:border-ak-wintergreen focus:ring-2 focus:ring-ak-wintergreen focus:ring-offset-2`;

	// Focus the first field when the dialog opens
	$effect(() => {
		nameInput?.focus();
	});

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') onClose();
	}

	function validate(): boolean {
		const next: Record<string, string> = {};
		if (name.trim().length < 2) next.name = 'Please enter your name';
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Please enter a valid email address';
		if (!/^[+]?[0-9]{10,15}$/.test(phone.replace(/[\s()-]/g, ''))) next.phone = 'Enter a valid mobile number';
		errors = next;
		return Object.keys(next).length === 0;
	}

	async function handleSubmit(event: SubmitEvent) {
		event.preventDefault();
		if (formState === 'submitting') return;
		errorMessage = '';

		if (!validate()) return;

		if (turnstileEnabled && !turnstileToken) {
			errors = { ...errors, captcha: 'Please complete the security check' };
			return;
		}

		formState = 'submitting';
		try {
			const result = await submitEnrollment({
				name: name.trim(),
				email: email.trim(),
				phone: phone.replace(/[\s()-]/g, ''),
				courses: [course.slug],
				experienceLevel: course.level,
				honeypot,
				turnstileToken,
				metadata: {
					source: 'courses-enroll-dialog',
					sessionDate: course.cohort.startDate
				}
			});

			if (result.success) {
				formState = 'success';
			} else {
				formState = 'error';
				errorMessage = result.message ?? 'Something went wrong. Please try again.';
				if (errorMessage.includes('CAPTCHA')) {
					errors = { ...errors, captcha: errorMessage };
				}
				turnstileRef?.reset();
			}
		} catch {
			formState = 'error';
			errorMessage = 'Network error. Please try again.';
		}
	}
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
<div
	class="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
	role="presentation"
	onclick={(event) => {
		if (event.target === event.currentTarget) onClose();
	}}
>
	<div
		role="dialog"
		aria-modal="true"
		aria-labelledby="enroll-dialog-title"
		class="relative w-full max-w-md max-h-[90vh] overflow-y-auto no-scrollbar p-5 sm:p-6 bg-linear-card rounded-lg border border-ak-border-subtle shadow-lg"
	>
		<button
			type="button"
			onclick={onClose}
			class="absolute top-3 right-3 p-2 text-ak-text-muted hover:text-ak-text-primary transition-colors touch-manipulation"
			aria-label="Close dialog"
		>
			<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
				<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
			</svg>
		</button>

		{#if formState === 'success'}
			<div class="text-center py-4">
				<div class="w-14 h-14 mx-auto mb-4 rounded-full bg-ak-wintergreen-dim flex items-center justify-center">
					<svg class="w-7 h-7 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
				</div>
				<h2 id="enroll-dialog-title" class="font-display text-xl font-bold mb-2">You're in!</h2>
				<p class="text-sm text-ak-text-secondary leading-relaxed mb-1">
					We've reserved your seat for <strong class="text-ak-text-primary">{course.title}</strong> — <LocalSessionTime cohort={course.cohort} variant="full" />.
				</p>
				<p class="text-sm text-ak-text-muted mb-5">Check your email for the confirmation. We'll send the session link before we go live.</p>
				<a
					href={icsHref}
					download="{course.slug}.ics"
					class="block w-full py-3 mb-2 bg-ak-wintergreen text-ak-deep font-semibold text-center rounded-md hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation"
				>
					Add to calendar
				</a>
				<button
					type="button"
					onclick={onClose}
					class="w-full py-2.5 border border-ak-border-subtle text-ak-text-secondary rounded-md text-sm hover:border-ak-wintergreen transition-colors touch-manipulation"
				>
					Close
				</button>
			</div>
		{:else}
			<h2 id="enroll-dialog-title" class="font-display text-xl font-bold mb-1 pr-8">Enroll — it's free</h2>
			<p class="text-sm text-ak-text-muted mb-5">
				{course.title} · <LocalSessionTime cohort={course.cohort} variant="full" />
			</p>

			{#if formState === 'error' && errorMessage}
				<div class="mb-4 p-3 bg-ak-error/10 border border-ak-error/20 rounded-md text-sm text-ak-error" role="alert" aria-live="assertive">
					{errorMessage}
				</div>
			{/if}

			<form onsubmit={handleSubmit} novalidate>
				<div class="space-y-4">
					<div>
						<label for="enroll-name" class="block text-sm font-medium mb-1.5">Name</label>
						<input
							id="enroll-name"
							type="text"
							autocomplete="name"
							placeholder="Your name"
							bind:this={nameInput}
							bind:value={name}
							class={inputClass(!!errors.name)}
						/>
						{#if errors.name}<p class="text-xs text-ak-error mt-1">{errors.name}</p>{/if}
					</div>

					<div>
						<label for="enroll-email" class="block text-sm font-medium mb-1.5">Email</label>
						<input
							id="enroll-email"
							type="email"
							autocomplete="email"
							placeholder="you@example.com"
							bind:value={email}
							class={inputClass(!!errors.email)}
						/>
						{#if errors.email}<p class="text-xs text-ak-error mt-1">{errors.email}</p>{/if}
					</div>

					<div>
						<label for="enroll-phone" class="block text-sm font-medium mb-1.5">Mobile number</label>
						<input
							id="enroll-phone"
							type="tel"
							autocomplete="tel"
							placeholder="+91 98765 43210"
							bind:value={phone}
							class={inputClass(!!errors.phone)}
						/>
						{#if errors.phone}<p class="text-xs text-ak-error mt-1">{errors.phone}</p>{/if}
					</div>

					<!-- Honeypot -->
					<input
						type="text"
						name="company_website"
						bind:value={honeypot}
						class="hidden"
						tabindex={-1}
						autocomplete="off"
						aria-hidden="true"
					/>

					{#if turnstileEnabled}
						<div>
							<Turnstile bind:token={turnstileToken} bind:this={turnstileRef} size="flexible" />
							{#if errors.captcha}<p class="text-xs text-ak-error mt-1">{errors.captcha}</p>{/if}
						</div>
					{/if}
				</div>

				<button
					type="submit"
					disabled={formState === 'submitting'}
					class="w-full py-3 mt-5 bg-ak-wintergreen text-ak-deep font-semibold text-center rounded-md hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation disabled:opacity-60"
				>
					{formState === 'submitting' ? 'Enrolling…' : 'Confirm Enrollment'}
				</button>

				<p class="text-xs text-ak-text-muted text-center mt-3">
					No fee. We'll only use your details for this session.
				</p>
			</form>
		{/if}
	</div>
</div>
